import { FilterOutlined } from '@ant-design/icons'
import { useRouter } from '@tanstack/react-router'
import { Input, Segmented, Select } from 'antd'
import { useUnresolvedThreadCounts } from 'features/libra/comments/commentsApi'
import { type LifecycleConfig } from 'features/libra/lifecycle/lifecycleTypes'
import { type LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import {
  ELIGIBILITY_STATUS,
  ELIGIBILITY_UI_LABELS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import type { FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { createLibraSearch } from 'routes/libra/-searchParams'
import styled from 'styled-components'

import { AssetListView } from './AssetListView'
import { CreatorFilterButton } from './CreatorFilterButton'
import { LifecycleBoardView } from './LifecycleBoardView'

type ViewMode = 'list' | 'board'
const VIEW_MODE_STORAGE_KEY = 'libra-dashboard-view-mode'

/** The asset list and lifecycle board shown when a dashboard has no `ticketDashboard` config. */
export const LegacyDashboardViews: FC<{
  latestTickets: ReadonlyArray<LibraTicket>
  tickets: ReadonlyArray<LibraTicket>
  allTickets: ReadonlyArray<LibraTicket>
  statsTickets: ReadonlyArray<LibraTicket>
  workflows: ReadonlyArray<LibraWorkflow>
  workflowMap: ReadonlyMap<string, LibraWorkflow>
  isLoading: boolean
  onRefresh: () => Promise<void>
  canCreateVersion: (ticket: LibraTicket) => boolean
}> = ({
  latestTickets,
  tickets,
  allTickets,
  statsTickets,
  workflows,
  workflowMap,
  isLoading,
  onRefresh,
  canCreateVersion,
}) => {
  const latestTicketIds = useMemo(
    () => latestTickets.map((t) => t.id),
    [latestTickets]
  )
  const { unresolvedCounts } = useUnresolvedThreadCounts(latestTicketIds)

  const lifecycleConfig = useMemo<LifecycleConfig | undefined>(() => {
    for (const w of workflows) {
      if (w.lifecycleConfig) return w.lifecycleConfig
    }
    return undefined
  }, [workflows])

  const [viewMode, setViewModeRaw] = useState<ViewMode>(() => {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    return stored === 'board' ? 'board' : 'list'
  })

  const setViewMode = useCallback((mode: ViewMode) => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
    setViewModeRaw(mode)
  }, [])
  const [filterText, setFilterText] = useState('')
  const [selectedEligibility, setSelectedEligibility] = useState<
    EligibilityStatus | undefined
  >(undefined)
  const [selectedCreators, setSelectedCreators] = useState<string[]>([])
  const [selectedStage, setSelectedStage] = useState<string | undefined>(
    undefined
  )

  const creatorEmails = useMemo(() => {
    const emails = new Set<string>()
    for (const t of tickets) {
      if (t.createdBy) emails.add(t.createdBy)
    }
    return [...emails].sort()
  }, [tickets])

  const stageOptions = useMemo(() => {
    if (!lifecycleConfig) return []
    return lifecycleConfig.stages.map((stage) => ({
      value: stage.id,
      label: stage.name,
    }))
  }, [lifecycleConfig])

  const handleStageSelect = useCallback((value: string) => {
    setSelectedStage((prev) => (prev === value ? undefined : value))
  }, [])

  const handleStageClear = useCallback(() => setSelectedStage(undefined), [])

  const router = useRouter()
  const handleBoardTicketClick = useCallback(
    (ticket: LibraTicket) => {
      const orgId = ticket.tags.organization as string | undefined
      const workflowId = ticket.workflowId
      if (!orgId || !workflowId) return
      router.navigate({
        to: '/libra',
        search: createLibraSearch({
          orgId,
          workflowId,
          ticket: ticket.id,
          thread: undefined,
        }),
      })
    },
    [router]
  )

  return (
    <>
      <ViewToolbar>
        <ViewFilters>
          <FilterInput
            placeholder='Filter assets'
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            allowClear
            prefix={<FilterIcon />}
          />
          {viewMode === 'list' && (
            <CreatorFilterButton
              emails={creatorEmails}
              selectedEmails={selectedCreators}
              onChange={setSelectedCreators}
            />
          )}
          {viewMode === 'list' && lifecycleConfig && (
            <StageSelect
              placeholder='Stage'
              value={selectedStage}
              onSelect={handleStageSelect}
              onClear={handleStageClear}
              allowClear
              suffixIcon={<FilterIcon />}
              options={stageOptions}
            />
          )}
          <EligibilitySelect
            placeholder='NII Eligibility'
            value={selectedEligibility}
            onSelect={(value: EligibilityStatus) => {
              if (selectedEligibility === value) {
                setSelectedEligibility(undefined)
              } else {
                setSelectedEligibility(value)
              }
            }}
            onClear={() => setSelectedEligibility(undefined)}
            allowClear
            suffixIcon={<FilterIcon />}
            options={[
              {
                value: ELIGIBILITY_STATUS.ELIGIBLE,
                label: ELIGIBILITY_UI_LABELS[ELIGIBILITY_STATUS.ELIGIBLE],
              },
              {
                value: ELIGIBILITY_STATUS.NOT_ELIGIBLE,
                label: ELIGIBILITY_UI_LABELS[ELIGIBILITY_STATUS.NOT_ELIGIBLE],
              },
              {
                value: ELIGIBILITY_STATUS.UNCLEAR,
                label: ELIGIBILITY_UI_LABELS[ELIGIBILITY_STATUS.UNCLEAR],
              },
            ]}
          />
        </ViewFilters>
        {lifecycleConfig && (
          <ViewToggle
            value={viewMode}
            onChange={(val) => setViewMode(val as ViewMode)}
            options={[
              { label: 'List', value: 'list' },
              { label: 'Board', value: 'board' },
            ]}
          />
        )}
      </ViewToolbar>
      {viewMode === 'board' && lifecycleConfig ? (
        <LifecycleBoardView
          lifecycleConfig={lifecycleConfig}
          tickets={tickets}
          allTickets={allTickets}
          workflowMap={workflowMap}
          onTicketClick={handleBoardTicketClick}
          onRefresh={onRefresh}
          filterText={filterText}
          selectedEligibility={selectedEligibility}
          canCreateVersion={canCreateVersion}
          unresolvedCounts={unresolvedCounts}
        />
      ) : (
        <AssetListView
          tickets={tickets}
          allTickets={allTickets}
          statsTickets={statsTickets}
          isLoading={isLoading}
          onRefresh={onRefresh}
          filterText={filterText}
          selectedEligibility={selectedEligibility}
          selectedCreators={selectedCreators}
          selectedStage={selectedStage}
          lifecycleConfig={lifecycleConfig}
          canCreateVersion={canCreateVersion}
          unresolvedCounts={unresolvedCounts}
        />
      )}
    </>
  )
}

const ViewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-shrink: 0;
  height: 32px;

  > * {
    height: 100%;
  }
`

const ViewFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  height: 100%;

  > * {
    height: 100%;
  }
`

const FilterInput = styled(Input)`
  flex: 1 1 0;
  min-width: 180px;
  display: flex;
  align-items: center;
`

const FilterIcon = styled(FilterOutlined)`
  color: ${({ theme }) => theme.colors.gray[700]};
`

const StageSelect = styled(Select)`
  flex-shrink: 0;
  width: 180px;
` as typeof Select

const EligibilitySelect = styled(Select)`
  flex-shrink: 0;
  width: 200px;
` as typeof Select

const ViewToggle = styled(Segmented)`
  flex-shrink: 0;
`
